import { pb } from './pocketbase';
import { createNotification } from './notification.service';

// Rechercher des étudiants par nom, email ou téléphone
export const searchStudents = async (query) => {
    try {
        if (!query || query.length < 2) {
            return [];
        }
        
        // Séparer la recherche en mots pour une recherche multi-termes
        const searchTerms = query.trim().split(/\s+/).filter(term => term.length > 0);
        
        // Construire le filtre : chaque mot doit matcher un des champs
        const termFilters = searchTerms.map(term => 
            `(first_name ~ "${term}" || last_name ~ "${term}" || email ~ "${term}" || phone ~ "${term}")`
        );
        const searchFilter = termFilters.join(' && ');
        
        const result = await pb.collection('students').getList(1, 10, {
            filter: searchFilter,
            expand: 'formation' 
        });
        
        return result.items;
    } catch (error) {
        console.error('Erreur lors de la recherche des étudiants:', error);
        return [];
    }
};

// Récupérer tous les étudiants d'une promotion
export const getStudentsByPromotion = async (promotionId) => {
    try {
        if (!promotionId) {
            return [];
        }
        
        const result = await pb.collection('students').getFullList({
            filter: `promotion_id = "${promotionId}"`,
            sort: 'first_name,last_name',
            expand: 'formation'
        });
        
        return result;
    } catch (error) {
        console.error('Erreur lors de la récupération des étudiants de la promotion:', error);
        return [];
    }
};

export const getStudentById = async (recordId, options = {}) => {
    try {
        const record = await pb.collection('students').getOne(recordId, options);
        return record;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'étudiant:', error);
        throw error;
    }
};

export const getStudentList = async (options = {}) => {
    try {
        // Préparation des options de requête avec expansion de la relation formation
        const queryOptions = {
            expand: 'formation, company_id',  // Expansion automatique de la relation formation
            sort: options.sort || '-created',
            filter: options.filter || '',
            page: options.page || 1,
            perPage: options.perPage || 20,
            fullData: options.fullData || false
        };
        
        // Préparation des options pour PocketBase
        const pbOptions = {
            expand: queryOptions.expand,
            sort: queryOptions.sort,
            filter: queryOptions.filter
        };
        
        if (!queryOptions.fullData) {
            pbOptions.fields = options.fields || 'id,first_name,last_name,email,phone,type,inscription_at,status,expand.formation.exact_title_of_qualification,expand.company_id.name,file_status';
        }
        
        // Récupération des étudiants avec leurs formations en une seule requête
        const result = await pb.collection('students').getList(
            queryOptions.page,
            queryOptions.perPage,
            pbOptions
        );
        
        // Retourner les résultats déjà formatés par PocketBase
        return {
            items: result.items,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            page: result.page,
            perPage: queryOptions.perPage
        };
    } catch (error) {
        console.error('Erreur lors de la récupération de la liste des étudiants:', error);
        
        // En cas d'erreur, retourner une liste vide dans le format attendu
        return {
            items: [],
            totalItems: 0,
            page: 1,
            perPage: 20,
            totalPages: 0
        };
    }
};

export const updateStudent = async (recordId, data) => {
    try {
       
        if (pb.authStore.isValid && pb.authStore.model) {
            // Récupérer l'étudiant actuel pour comparer l'email
            const currentStudent = await pb.collection('students').getOne(recordId);
            
            const updatedStudent = await pb.collection('students').update(recordId, data);
            
            // Synchroniser l'email avec la collection users si l'email a changé
            if (data.email && data.email !== currentStudent.email) {
                try {
                    // Trouver l'utilisateur lié via user_ref ou student_id
                    let userId = currentStudent.user_ref;
                    
                    if (!userId) {
                        // Fallback: chercher l'utilisateur par student_id
                        const users = await pb.collection('users').getList(1, 1, {
                            filter: `student_id="${recordId}"`
                        });
                        if (users.items.length > 0) {
                            userId = users.items[0].id;
                        }
                    }
                    
                    if (userId) {
                        await pb.collection('users').update(userId, { 
                            email: data.email 
                        });
                        console.log('Email synchronisé avec la collection users');
                    }
                } catch (syncError) {
                    console.error('Erreur lors de la synchronisation de l\'email avec users:', syncError);
                    // Ne pas faire échouer la mise à jour de l'étudiant si la sync échoue
                }
            }
            
            return updatedStudent;
        } else {
            console.error('Aucun utilisateur authentifié. Impossible de mettre à jour l\'étudiant sans partner_id.');
            return null;
        }    
    } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'étudiant:', error);
        throw error;
    }
};

export const updateStudentFileStatus = async (studentId, fileStatus) => {
    try {
        if (!studentId) {
            throw new Error('ID étudiant non fourni');
        }
        
        // Valider le statut de fichier
        if (!['Complet', 'Incomplet'].includes(fileStatus)) {
            throw new Error('Statut de fichier invalide. Utilisez "Complet" ou "Incomplet"');
        }
                
        // Mettre à jour uniquement le statut du dossier sans modifier les autres champs
        const updatedStudent = await pb.collection('students').update(studentId, {
            file_status: fileStatus
        });
        
        // Retourner l'étudiant mis à jour avec toutes ses informations
        return updatedStudent;
    } catch (error) {
        console.error('Erreur lors de la mise à jour du statut de dossier:', error);
        throw error;
    }
};

export const getStudentForCurrentUser = async () => {
    try {
        // Vérifier si l'utilisateur est connecté
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non connecté');
        }
        
        const authData = pb.authStore.model;
        
        // Vérifier si l'utilisateur a un student_id
        if (!authData.student_id) {
            throw new Error('Aucun étudiant associé à cet utilisateur');
        }
        
        // Récupérer directement l'étudiant par son ID
        const student = await pb.collection('students').getOne(authData.student_id);
        
        return student;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'étudiant:', error);
        throw error;
    }
};

export const notificationFolderIncomplete = async (userRef) => {
    try {
        await createNotification({
            type: 'folder_incomplete',
            title: 'Compléter votre dossier',
            message: 'Votre dossier est incomplet. Veuillez le compléter pour pouvoir accéder à vos formations.',
            icon: 'contract_logo.png',
            recipient_id: userRef,
            link: '/student/account',
            read: false
        });
        
        return { success: true };
    } catch (error) {
        console.error('Erreur lors de l\'envoi de la notification de relance:', error);
        throw error;
    }
};

export const searchProspectsForOffer = async (query = '') => {
    try {
      const consentFilter = `consent_to_share_with_companies = true`;
      const companyFilter = `company = null`; 
      
      let filter = `${consentFilter} && ${companyFilter}`;
      
      if (query && query.trim().length >= 2) {
        // Séparer la recherche en mots pour une recherche multi-termes
        const searchTerms = query.trim().split(/\s+/).filter(term => term.length > 0);
        
        // Construire le filtre : chaque mot doit matcher un des champs
        const termFilters = searchTerms.map(term => 
          `(first_name ~ "${term}" || last_name ~ "${term}" || email ~ "${term}" || phone ~ "${term}")`
        );
        const searchFilter = termFilters.join(' && ');
        filter = `${filter} && ${searchFilter}`;
      }
  
      const result = await pb.collection('students').getList(1, 50, {
        filter,
        sort: '-created' 
      });
      
      console.log(`Prospects trouvés avec consentement et sans entreprise:`, result.items?.length || 0);
      return result.items;
    } catch (error) {
      console.error('Erreur lors de la recherche des prospects pour offre:', error);
      return [];
    }
  };

export const sendStudentCredentials = async (studentId) => {
    try {
        // Récupérer l'étudiant pour obtenir son email
        const student = await pb.collection('students').getOne(studentId);
        
        if (!student || !student.email) {
            throw new Error('Email de l\'étudiant non trouvé');
        }
        
        // Utiliser la même mécanique que RequestPasswordReset mais avec l'email de l'étudiant
        await pb.collection('users').requestPasswordReset(student.email, {
            emailTemplate: 'password_reset',
            // URL de réinitialisation pointant vers le frontend
            actionUrl: `${window.location.origin}/reset-password?token={TOKEN}`
        });
        
        return { success: true, message: 'Email de réinitialisation envoyé avec succès' };
    } catch (error) {
        console.error('Erreur lors de l\'envoi des identifiants:', error);
        throw error;
    }
};

// Récupérer le décompte des éléments liés à un étudiant (pour la modal de confirmation)
export const getStudentDeletionPreview = async (studentId) => {
    try {
        const preview = {
            student: null,
            documents: 0,
            memos: 0,
            folders: 0,
            contracts: 0,
            attendances: 0,
            sessionGroupMembers: 0,
            user: null
        };

        // Récupérer l'étudiant
        preview.student = await pb.collection('students').getOne(studentId);

        // Compter les documents
        const documents = await pb.collection('student_documents').getList(1, 1, {
            filter: `student_id="${studentId}"`
        });
        preview.documents = documents.totalItems;

        // Compter les mémos
        const memos = await pb.collection('student_memos').getList(1, 1, {
            filter: `student_id="${studentId}"`
        });
        preview.memos = memos.totalItems;

        // Compter les folders
        const folders = await pb.collection('folders').getList(1, 1, {
            filter: `student_id="${studentId}"`
        });
        preview.folders = folders.totalItems;

        // Compter les contrats
        const contracts = await pb.collection('contracts').getList(1, 1, {
            filter: `student_id="${studentId}"`
        });
        preview.contracts = contracts.totalItems;

        // Compter les présences
        const attendances = await pb.collection('attendances').getList(1, 1, {
            filter: `participant_id="${studentId}"`
        });
        preview.attendances = attendances.totalItems;

        // Compter les appartenances aux groupes de sessions
        const sessionGroupMembers = await pb.collection('session_group_members').getList(1, 1, {
            filter: `student_id="${studentId}"`
        });
        preview.sessionGroupMembers = sessionGroupMembers.totalItems;

        // Vérifier s'il y a un utilisateur lié
        try {
            const users = await pb.collection('users').getList(1, 1, {
                filter: `student_id="${studentId}"`
            });
            if (users.totalItems > 0) {
                preview.user = users.items[0];
            }
        } catch (e) {
            console.log('Pas d\'utilisateur lié ou erreur de récupération');
        }

        return preview;
    } catch (error) {
        console.error('Erreur lors de la prévisualisation de suppression:', error);
        throw error;
    }
};

// Supprimer un étudiant et toutes ses données liées (suppression en cascade)
export const deleteStudent = async (studentId) => {
    try {
        // 1. Supprimer les présences (attendances)
        const attendances = await pb.collection('attendances').getFullList({
            filter: `participant_id="${studentId}"`
        });
        for (const attendance of attendances) {
            await pb.collection('attendances').delete(attendance.id);
        }

        // 2. Supprimer les appartenances aux groupes de sessions
        const sessionGroupMembers = await pb.collection('session_group_members').getFullList({
            filter: `student_id="${studentId}"`
        });
        for (const member of sessionGroupMembers) {
            await pb.collection('session_group_members').delete(member.id);
        }

        // 3. Supprimer les mémos
        const memos = await pb.collection('student_memos').getFullList({
            filter: `student_id="${studentId}"`
        });
        for (const memo of memos) {
            await pb.collection('student_memos').delete(memo.id);
        }

        // 4. Supprimer les documents
        const documents = await pb.collection('student_documents').getFullList({
            filter: `student_id="${studentId}"`
        });
        for (const doc of documents) {
            await pb.collection('student_documents').delete(doc.id);
        }

        // 5. Supprimer les folders
        const folders = await pb.collection('folders').getFullList({
            filter: `student_id="${studentId}"`
        });
        for (const folder of folders) {
            await pb.collection('folders').delete(folder.id);
        }

        // 6. Supprimer les contrats
        const contracts = await pb.collection('contracts').getFullList({
            filter: `student_id="${studentId}"`
        });
        for (const contract of contracts) {
            await pb.collection('contracts').delete(contract.id);
        }

        // 7. Supprimer l'utilisateur lié
        try {
            const users = await pb.collection('users').getFullList({
                filter: `student_id="${studentId}"`
            });
            for (const user of users) {
                await pb.collection('users').delete(user.id);
            }
        } catch (e) {
            console.log('Pas d\'utilisateur lié ou erreur de suppression:', e);
        }

        // 8. Supprimer l'étudiant
        await pb.collection('students').delete(studentId);

        return { success: true, message: 'Étudiant et données liées supprimés avec succès' };
    } catch (error) {
        console.error('Erreur lors de la suppression de l\'étudiant:', error);
        throw error;
    }
};
